import { Vector3 } from "@babylonjs/core/Maths/math.vector.pure.js";
import { Color3 } from "@babylonjs/core/Maths/math.color.pure.js";
import { ToHalfFloat } from "@babylonjs/core/Misc/halfFloat.js";
import { daylightRadiance } from "./atmosphere.ts";

/**
 * CPU half of the daylight IBL (`lighting.ts` uploads it): the sky cube's linear
 * radiance per mip level, GGX-filtered. Imports only Babylon's maths modules, so
 * the bake worker can run it off the main thread; rows and faces are independent.
 */
export const ENVIRONMENT_SIZE = 256;
const FILTER_SAMPLES = 128;
/** Babylon's roughness-to-mip mapping, also used by its HDRFiltering implementation. */
export const LOD_SCALE = 0.8;
/** Mip levels from ENVIRONMENT_SIZE down to 1. */
const LEVELS = Math.log2(ENVIRONMENT_SIZE) + 1;

type Rgb = readonly [number, number, number];
/** A sky as plain numbers, so it can be posted to a worker. */
export interface SkyInputs {
  readonly zenith: Rgb;
  readonly horizon: Rgb;
  /** Unit vector towards the sun. */
  readonly sun: Rgb;
}
/** Linear RGB (three doubles per texel, row-major) for `[level][face]`. */
export type SkyRadiance = Float64Array[][];

function cubeDirection(face: number, u: number, v: number, out: Vector3): void {
  switch (face) {
    case 0: out.set(1, -v, -u); break;
    case 1: out.set(-1, -v, u); break;
    case 2: out.set(u, 1, v); break;
    case 3: out.set(u, -1, -v); break;
    case 4: out.set(u, -v, 1); break;
    default: out.set(-u, -v, -1); break;
  }
  out.normalize();
}

function radicalInverse(bits: number): number {
  bits = (bits << 16) | (bits >>> 16);
  bits = ((bits & 0x55555555) << 1) | ((bits & 0xaaaaaaaa) >>> 1);
  bits = ((bits & 0x33333333) << 2) | ((bits & 0xcccccccc) >>> 2);
  bits = ((bits & 0x0f0f0f0f) << 4) | ((bits & 0xf0f0f0f0) >>> 4);
  bits = ((bits & 0x00ff00ff) << 8) | ((bits & 0xff00ff00) >>> 8);
  return (bits >>> 0) / 4294967296;
}

/** Level-0 radiance of rows [y0, y1) of one face: the volumetric sky, evaluated once per texel. */
export function skyBaseRows(sky: SkyInputs, face: number, y0: number, y1: number): Float64Array {
  const size = ENVIRONMENT_SIZE;
  const zenith = new Color3(...sky.zenith);
  const horizon = new Color3(...sky.horizon);
  const sun = new Vector3(...sky.sun);
  const direction = Vector3.Zero();
  const radiance = Color3.Black();
  const out = new Float64Array((y1 - y0) * size * 3);
  for (let y = y0, o = 0; y < y1; y++) {
    for (let x = 0; x < size; x++, o += 3) {
      cubeDirection(face, 2 * (x + 0.5) / size - 1, 2 * (y + 0.5) / size - 1, direction);
      daylightRadiance(direction.x, direction.y, direction.z, zenith, horizon, sun, radiance);
      out[o] = radiance.r; out[o + 1] = radiance.g; out[o + 2] = radiance.b;
    }
  }
  return out;
}

/**
 * GGX importance sampling: levels 1… of one face are roughness lobes, not ordinary
 * box-filtered sky. `base` is the level-0 radiance of all six faces at float precision.
 */
export function skyFilteredLevels(face: number, base: readonly Float32Array[]): Float64Array[] {
  const direction = Vector3.Zero();
  const radiance = Color3.Black();
  const samples = new Float32Array(FILTER_SAMPLES * 4);
  const levels: Float64Array[] = [];
  for (let lod = 1, size = ENVIRONMENT_SIZE >> 1; lod < LEVELS; lod++, size >>= 1) {
    // Roughness saturates at one; unused coarser mips retain that widest GGX lobe.
    const alpha = Math.min(1, Math.pow(2, lod / LOD_SCALE) / ENVIRONMENT_SIZE);
    for (let i = 0; i < FILTER_SAMPLES; i++) {
      const u = (i + 0.5) / FILTER_SAMPLES;
      const phi = 2 * Math.PI * radicalInverse(i);
      const cosTheta = Math.sqrt((1 - u) / (1 + (alpha * alpha - 1) * u));
      const sinTheta = Math.sqrt(1 - cosTheta * cosTheta);
      // Reflect N about the sampled half-vector, in N's local frame.
      const j = i * 4;
      samples[j] = 2 * cosTheta * sinTheta * Math.cos(phi);
      samples[j + 1] = 2 * cosTheta * sinTheta * Math.sin(phi);
      samples[j + 2] = 2 * cosTheta * cosTheta - 1;
      samples[j + 3] = Math.max(0, samples[j + 2]!);
    }
    const out = new Float64Array(size * size * 3);
    for (let y = 0, o = 0; y < size; y++) {
      for (let x = 0; x < size; x++, o += 3) {
        cubeDirection(face, 2 * (x + 0.5) / size - 1, 2 * (y + 0.5) / size - 1, direction);
        const nx = direction.x;
        const ny = direction.y;
        const nz = direction.z;
        const horizontal = Math.hypot(nx, nz);
        const tx = horizontal > 0.001 ? nz / horizontal : 1;
        const tz = horizontal > 0.001 ? -nx / horizontal : 0;
        const bx = ny * tz;
        const by = nz * tx - nx * tz;
        const bz = -ny * tx;
        let r = 0;
        let g = 0;
        let b = 0;
        let total = 0;
        for (let i = 0; i < FILTER_SAMPLES; i++) {
          const j = i * 4;
          const weight = samples[j + 3]!;
          if (weight === 0) continue;
          const lx = samples[j]!;
          const ly = samples[j + 1]!;
          const lz = samples[j + 2]!;
          sampleSkyCube(base, tx * lx + bx * ly + nx * lz, by * ly + ny * lz, tz * lx + bz * ly + nz * lz, radiance);
          r += radiance.r * weight; g += radiance.g * weight; b += radiance.b * weight;
          total += weight;
        }
        out[o] = r / total; out[o + 1] = g / total; out[o + 2] = b / total;
      }
    }
    levels.push(out);
  }
  return levels;
}

/** Every level and face on the calling thread. */
export function bakeSkyRadiance(sky: SkyInputs): SkyRadiance {
  const base = Array.from({ length: 6 }, (_, face) => skyBaseRows(sky, face, 0, ENVIRONMENT_SIZE));
  const linear = base.map((face) => Float32Array.from(face));
  const filtered = linear.map((_, face) => skyFilteredLevels(face, linear));
  return [base, ...filtered[0]!.map((_, level) => filtered.map((face) => face[level]!))];
}

/** RGBA texel data per `[level][face]`: half floats when `hdr`, otherwise clamped bytes. */
export function encodeSky(radiance: SkyRadiance, hdr: boolean): ArrayBufferView[][] {
  return radiance.map((faces) => faces.map((rgb) => {
    const texels = rgb.length / 3;
    const data = hdr ? new Uint16Array(texels * 4) : new Uint8Array(texels * 4);
    for (let t = 0; t < texels; t++) {
      const r = rgb[t * 3]!, g = rgb[t * 3 + 1]!, b = rgb[t * 3 + 2]!;
      const p = t * 4;
      data[p] = hdr ? ToHalfFloat(r) : Math.round(Math.min(1, r) * 255);
      data[p + 1] = hdr ? ToHalfFloat(g) : Math.round(Math.min(1, g) * 255);
      data[p + 2] = hdr ? ToHalfFloat(b) : Math.round(Math.min(1, b) * 255);
      data[p + 3] = hdr ? ToHalfFloat(1) : 255;
    }
    return data;
  }));
}

/** Bilinear linear-radiance lookup in the cube's shared Babylon orientation. */
function sampleSkyCube(faces: readonly Float32Array[], x: number, y: number, z: number, out: Color3): void {
  const ax = Math.abs(x), ay = Math.abs(y), az = Math.abs(z);
  let face: number, u: number, v: number;
  if (ax >= ay && ax >= az) {
    face = x > 0 ? 0 : 1; u = (x > 0 ? -z : z) / ax; v = -y / ax;
  } else if (ay >= az) {
    face = y > 0 ? 2 : 3; u = x / ay; v = (y > 0 ? z : -z) / ay;
  } else {
    face = z > 0 ? 4 : 5; u = (z > 0 ? x : -x) / az; v = -y / az;
  }
  const px = Math.max(0, Math.min(ENVIRONMENT_SIZE - 1, (u * 0.5 + 0.5) * ENVIRONMENT_SIZE - 0.5));
  const py = Math.max(0, Math.min(ENVIRONMENT_SIZE - 1, (v * 0.5 + 0.5) * ENVIRONMENT_SIZE - 0.5));
  const ix = Math.floor(px), iy = Math.floor(py), fx = px - ix, fy = py - iy;
  const nx = Math.min(ix + 1, ENVIRONMENT_SIZE - 1), ny = Math.min(iy + 1, ENVIRONMENT_SIZE - 1);
  const a = (iy * ENVIRONMENT_SIZE + ix) * 3, b = (iy * ENVIRONMENT_SIZE + nx) * 3;
  const c = (ny * ENVIRONMENT_SIZE + ix) * 3, d = (ny * ENVIRONMENT_SIZE + nx) * 3;
  const data = faces[face]!;
  const wa = (1 - fx) * (1 - fy), wb = fx * (1 - fy), wc = (1 - fx) * fy, wd = fx * fy;
  out.set(data[a]! * wa + data[b]! * wb + data[c]! * wc + data[d]! * wd,
    data[a + 1]! * wa + data[b + 1]! * wb + data[c + 1]! * wc + data[d + 1]! * wd,
    data[a + 2]! * wa + data[b + 2]! * wb + data[c + 2]! * wc + data[d + 2]! * wd);
}
